import type { IconId } from "@/components/ui/Icon";

export type SidebarViewId =
  | 'all'
  | 'today'
  | 'todo'
  | 'pinned'
  | 'trash'
  | 'archive'
  | 'untagged'
  | 'thisWeek'
  | 'shared'
  | 'sharedWithMe'
  | 'attachments'

export interface SidebarViewDef {
  id: SidebarViewId
  label: string
  icon: IconId
  /**
   * Query-string fragment for this view's sidebar link.
   *  - `null` means the view does not use `?view=` (only `all`, which navigates to the base path)
   *  - any other view returns `view=<id>` so the sidebar NavItem wires up correctly
   */
  queryString: string | null
}

export const SIDEBAR_VIEWS: Record<SidebarViewId, SidebarViewDef> = {
  all: { id: 'all', label: 'All Notes', icon: "note", queryString: null },
  today: { id: 'today', label: 'Today', icon: "clock", queryString: 'view=today' },
  todo: { id: 'todo', label: 'Todo', icon: "check-circle", queryString: 'view=todo' },
  pinned: { id: 'pinned', label: 'Pinned', icon: "pin", queryString: 'view=pinned' },
  untagged: { id: 'untagged', label: 'Untagged', icon: "tag", queryString: 'view=untagged' },
  thisWeek: { id: 'thisWeek', label: 'This Week', icon: "calendar", queryString: 'view=thisWeek' },
  shared: { id: 'shared', label: 'Shared by me', icon: "share", queryString: 'view=shared' },
  sharedWithMe: { id: 'sharedWithMe', label: 'Shared with me', icon: "inbox", queryString: 'view=sharedWithMe' },
  attachments: { id: 'attachments', label: 'Attachments', icon: "attachment", queryString: 'view=attachments' },
  archive: { id: 'archive', label: 'Archive', icon: "archive", queryString: 'view=archive' },
  trash: { id: 'trash', label: 'Trash', icon: "trash", queryString: 'view=trash' },
}

/**
 * `smart_view_selected` `view` vocabulary. Pinned to the names in `ANALYTICS.md`
 * rather than derived from the id, because three views spell differently there
 * (`shared_by_me`, `this_week`, `archive`). Mirrors `NoteFilter.analyticsName`
 * on Swift.
 */
export const VIEW_ANALYTICS_NAME: Record<SidebarViewId, string> = {
  all: 'all',
  today: 'today',
  todo: 'todo',
  pinned: 'pinned',
  untagged: 'untagged',
  thisWeek: 'this_week',
  shared: 'shared_by_me',
  sharedWithMe: 'shared_with_me',
  attachments: 'attachments',
  archive: 'archive',
  trash: 'trash',
}

// Same order and defaults as the native apps (AppPreferences.swift).
export const DEFAULT_VIEW_ORDER: SidebarViewId[] = [
  'all',
  'today',
  'thisWeek',
  'todo',
  'pinned',
  'untagged',
  'shared',
  'sharedWithMe',
  'attachments',
  'archive',
  'trash',
]

export const DEFAULT_HIDDEN: SidebarViewId[] = [
  'today',
  'thisWeek',
  'todo',
  'pinned',
  'untagged',
  'shared',
  'sharedWithMe',
  'attachments',
]

/**
 * Merge stored order with `DEFAULT_VIEW_ORDER`.
 *  - Drops ids that no longer exist in `SIDEBAR_VIEWS`
 *  - Preserves user-custom order for known ids
 *  - Appends any newly-introduced views at the end (they land after the user's
 *    existing order, which typically puts them just before the implicit final
 *    `trash` — callers can re-order afterwards).
 */
export function reconcileViewOrder(stored: SidebarViewId[]): SidebarViewId[] {
  const seen = new Set<SidebarViewId>()
  const valid: SidebarViewId[] = []
  for (const id of stored) {
    if (id in SIDEBAR_VIEWS && !seen.has(id)) {
      valid.push(id)
      seen.add(id)
    }
  }
  const missing = DEFAULT_VIEW_ORDER.filter((id) => !seen.has(id))
  return [...valid, ...missing]
}
